<template>
  <form
    id="rb-form"
    @submit.prevent="submit"
  />
  <div
    id="content"
    class="flex-column"
  >
    <p class="title">
      Olá
    </p>
    <p class="text">
      Veja como é fácil financiar seus estudos.
    </p>
    <div class="course flex-column">
      <div class="flex-row">
        <img src="@/assets/student.svg">
        <p class="title capitalize">
          {{ simulation.course_offering?.name.toLowerCase() }}
        </p>
      </div>
      <div class="flex-row">
        <img src="@/assets/address.svg">
        <p class="capitalize">
          {{ address }}
        </p>
      </div>
      <div class="flex-row">
        <img src="@/assets/read-book.svg">
        <p class="capitalize">
          {{ simulation.course_offering?.mode?.name }}
        </p>
      </div>
    </div>
    <div class="simulation flex-column">
      <div class="prices flex-column">
        <p class="text">
          Na faculdade, você pagaria
        </p>
        <p class="text total-price">
          R$<span>{{ formValues.total }}</span>/mês
        </p>
        <p class="text">
          Com o Pravaler, você paga
        </p>
        <p class="text final-price">
          R$<span>{{ formValues.parcel }}</span>/mês
        </p>
      </div>
      <div class="tax flex-column">
        <div class="flex-row">
          <span class="text">Metade da mensalidade</span>
          <span class="text">R${{ formValues.half }}</span>
        </div>
        <div
          class="flex-row"
          v-if="simulation.scholarship_value > 0"
        >
          <span class="text">Bolsa</span>
          <span class="text"> ({{ formValues.scholarship }}%)</span>
        </div>

        <div class="flex-row">
          <span class="text">Juros</span>
          <span class="text"> ({{ formValues.interest_rate }}%)</span>
        </div>
        <hr>
        <div class="flex-row">
          <span class="text bold">Total da parcela</span>
          <span class="text bold">R${{ formValues.parcel }}</span>
        </div>
      </div>
      <div class="details flex-column">
        <div class="flex-row">
          <img src="@/assets/tag-price.svg">
          <p>
            Parcelas menores do que a mensalidade da faculdade
          </p>
        </div>
        <div class="flex-row">
          <img src="@/assets/percent.svg">
          <p>Uma das menores taxas do mercado</p>
        </div>
        <div class="flex-row">
          <img src="@/assets/reload-value.svg">
          <p>As parcelas não se acumulam</p>
        </div>
        <div class="flex-row">
          <img src="@/assets/calendar-check.svg">
          <p>Tenha mais tempo para pagar</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, reactive, ref } from 'vue';
import { getSimulation } from '@/api/Person';
import type { ISimulation } from './interface/ISimulation';
import type { ICampus } from './interface/ICampus';
import { stepStore, loaderStore, proposalStore, snackbarStore } from '@/store/app';

const simulation = ref<ISimulation>({
  id: 0,
  person_id: 0,
  course_offering_id: 0,
  product_id: 0,
  parcel_value_full: 0,
  parcel_value: 0,
  scholarship_value: 0,
  interest_fee: false,
  interest_rate: 0,
});

const formValues = reactive({
  parcel: computed(() => formatPrice(simulation.value.parcel_value)),
  total: computed(() => formatPrice(simulation.value.parcel_value_full)),
  half: computed(() => formatPrice(simulation.value.parcel_value_full / 2)),
  scholarship: computed(() => formatPrice(simulation.value.scholarship_value)),
  interest_fee: simulation.value.interest_fee,
  interest_rate: computed(() => formatPrice(simulation.value.interest_rate)),
});

onBeforeMount(getSimulationData);

async function getSimulationData() {
  try {
    loaderStore().setOpen(true);
    const data = await getSimulation(proposalStore().getProposal().simulation_id as number);
    simulation.value = data;
  } catch (error) {
    snackbarStore().alert('Tivemos um problema, aguarde alguns segundos e tente novamente!');
  } finally {
    loaderStore().setOpen(false);
  }
}

const address = computed(() => {
  if (simulation.value.course_offering?.campus) {
    const { name, city, uf } = simulation.value.course_offering?.campus as ICampus;
    return [name, city].join(' - ').toLowerCase().concat(' - ', uf);
  }
  return '';
});


function formatPrice(value: number) {
  return new Intl.NumberFormat('pt-BR', { currency: 'BRL', minimumFractionDigits: Number.isInteger(value) ? 0 : 2, maximumFractionDigits: 2 }).format(value);
}

function submit() {
  loaderStore().setOpen(true);
  stepStore().nextStep('student');
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}

#content {
  margin: 1.3rem 1.15rem;
  gap: 16px;

  .title {
    font-family: Poppins;
    font-size: 1rem;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0em;
    text-align: left;
  }

  .text {
    font-size: 0.90rem;
    font-weight: 400;
    line-height: 1.45rem;
    letter-spacing: 0.015em;
    text-align: left;
    color: rgba(77, 77, 77, 1);
  }

  .course {
    gap: 4px;

    &>div {
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      height: min-content;

      &>img {
        width: 16px;
      }

      &:not(&:first-of-type) {
        &>img {
          box-sizing: border-box;
          width: 12px;
          margin: auto 2px;
        }
      }

      &>p {
        &:not(.title) {
          font-size: 0.875rem;
          font-weight: 400;
          line-height: 1rem;
          letter-spacing: 0em;
          text-align: left;
        }
      }
    }
  }

  .simulation {
    width: 100%;
    background-color: color-mix(in srgb, var(--secondary) 10%, transparent);
    border: 1px solid var(--secondary);
    border-radius: 4px;
    padding: 24px;
    gap: 2rem;

    &>.prices {
      align-items: center;

      &>.total-price,
      &>.final-price {
        &>span {
          letter-spacing: 0.025em;
          text-align: center;
        }
      }

      &>.total-price {
        color: rgba(128, 128, 128, 1);

        &>span {
          font-size: 2rem;
          font-weight: 400;
          line-height: 3.5rem;
          text-decoration: line-through;
        }
      }

      &>.final-price {
        color: var(--secondary);

        &>span {
          font-size: 3rem;
          font-weight: 700;
          line-height: 4rem;
        }
      }
    }

    &>.tax {
      width: 100%;
      border: 1px solid rgba(162, 162, 162, 1);
      border-radius: 4px;
      padding: 14px 16px;

      &>div {
        margin: 4px 0;

        &>span {
          font-size: 12px;
          line-height: 14px;

          &:first-of-type {
            overflow: hidden;
            white-space: nowrap;

            &::after {
              content: " . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . "
            }
          }
        }
      }

      &>hr {
        margin: 7px 0;
      }
    }

    &>.details {
      gap: 1rem;

      &>div {
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
        height: min-content;

        &>img {
          width: 20px;
        }

        &>p {
          font-size: 0.75rem;
          color: rgba(26, 26, 26, 1);
          font-weight: 400;
          line-height: 0.875rem;
          text-align: left;
        }
      }
    }
  }
}
</style>
